import { Fragment, useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useFilters } from "../context/FilterContext";
import { useDateRange } from "../context/DateRangeContext";
import { BrandLogo, companyName } from "../components/BrandLogo";
import { branding } from "../config/branding";
import { motion } from "framer-motion";
import lottie from "lottie-web";
import moneyLoadingAnimation from "../assets/Cash.json";
import { Badge } from "../components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { DASHBOARD_TENANTS, buildOfferDashboardPath, type DashboardTenantDefinition } from "../config/dashboard-tenants";
import { getEmbeddedDashboardSlugByOfferName } from "../config/embedded-offers";

interface TrendPoint {
  date: string;
  value: number;
}

interface OfferData {
  _id: string;
  totalCalls: number;
  totalCash: number;
  totalRevenue: number;
  mrrCash: number;
  callsClosed: number;
  closeRate: number;
  showRate: number;
  avgCashPerCall: number;
  avgCashPerClose: number;
  trend: TrendPoint[];
  liveDashboardSlug?: string | null;
}

interface AllOffersTotals {
  offerCount: number;
  totalCalls: number;
  totalCash: number;
  totalRevenue: number;
  mrrCash: number;
  callsClosed: number;
  closeRate: number;
  showRate: number;
  avgCashPerCall: number;
  avgCashPerClose: number;
}

const SERVICE_PAYMENT_CATEGORIES = new Set(["Client Onboard", "Consulting", "Webinar Buildouts", "Ascend Content", "Apptics", "Affiliate Payouts"]);
const DASHBOARD_OFFERS = new Set(["Urban Unity", "Cr8Her", "The Artillery", "The Artillery Affiliate Academy", "Affiliate Academy", "Scaling Society", "Empire Blueprint", "Wholesale", "Wholesale Launchpad", "The Formula", "Ascend", "Cashflow Creator", "CEO Growth", "Marcus Fit", "The Fitness CEO", "Ecom Launcher", "Myles Of Luxury", "Miles of Luxury", "Baby Birkin"]);
const OFFER_DISPLAY_NAMES: Record<string, string> = { Wholesale: "Wholesale Launchpad", "Myles Of Luxury": "Miles of Luxury" };
type ServicePayment = { name: string; amount: number; detail?: string };
const SERVICE_PAYMENT_DETAILS: Record<string, ServicePayment[]> = {
  "Client Onboard": [
    { name: "Client Onboard EOC payment", amount: 5750 }, { name: "Client Onboard EOC payment", amount: 3750 },
    { name: "Client Onboard EOC payment", amount: 13750 }, { name: "Client Onboard EOC payment", amount: 10000 },
    { name: "Client Onboard EOC payment", amount: 2500 }, { name: "Client Onboard EOC payment", amount: 2500 },
    { name: "Client Onboard EOC payment", amount: 2500 }, { name: "Samantha Belanger", amount: 7000 }, { name: "Sawyer Fast", amount: 10000 },
  ],
  "Webinar Buildouts": [{ name: "Alex Beck", amount: 5000 }],
  "Consulting": [{ name: "Victor Reece George", amount: 6500 }, { name: "Soumil Kansal", amount: 13420 }],
  "Ascend Content": [{ name: "Ellie Snow", amount: 1546.61 }],
  "Apptics": [{ name: "Jai Thind", amount: 20000, detail: "December 2025" }, { name: "Jai Thind", amount: 20000, detail: "January 2026" }, { name: "Jai Thind", amount: 20000, detail: "February 2026" }],
};

function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return "99,102,241";
  const [, red = "63", green = "66", blue = "f1"] = result;
  return `${parseInt(red, 16)},${parseInt(green, 16)},${parseInt(blue, 16)}`;
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat("en-US").format(value);
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatCompact(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

function formatCashPerClose(value: number, callsClosed: number): string {
  return callsClosed > 0 ? formatCurrency(value) : "\u2014";
}

// SVG donut ring
function ShareRing({ percent, color, size = 42 }: { percent: number; color: string; size?: number }) {
  const strokeW = 3;
  const radius = (size - strokeW) / 2;
  const circumference = 2 * Math.PI * radius;
  const filled = (percent / 100) * circumference;
  const gap = circumference - filled;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="block">
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="currentColor" className="text-border/20" strokeWidth={strokeW} />
      <circle
        cx={size / 2} cy={size / 2} r={radius}
        fill="none" stroke={color} strokeWidth={strokeW} strokeLinecap="round"
        strokeDasharray={`${filled} ${gap}`}
        strokeDashoffset={circumference / 4}
        style={{ transition: "stroke-dasharray 0.8s ease-out" }}
      />
    </svg>
  );
}

// Mini sparkline chart
function Sparkline({ data, color, width = 80, height = 24 }: { data: TrendPoint[]; color: string; width?: number; height?: number }) {
  if (data.length < 2) return null;
  const values = data.map(d => d.value);
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;

  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width;
    const y = height - 2 - ((v - min) / range) * (height - 4);
    return `${x},${y}`;
  }).join(" ");

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="block">
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
        opacity="0.5"
      />
    </svg>
  );
}

function MoneyLoader() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const anim = lottie.loadAnimation({
      container: containerRef.current,
      renderer: "svg",
      loop: true,
      autoplay: true,
      animationData: moneyLoadingAnimation,
    });
    return () => anim.destroy();
  }, []);

  return <div ref={containerRef} style={{ width: 180, height: 180 }} />;
}

export function OfferSelection() {
  const [offers, setOffers] = useState<OfferData[]>([]);
  const [allOffersTotals, setAllOffersTotals] = useState<AllOffersTotals | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [selectedServiceTotal, setSelectedServiceTotal] = useState(0);
  const { setOffer, offer } = useFilters();
  const { formatForApi } = useDateRange();
  const navigate = useNavigate();
  const apiDates = formatForApi();

  useEffect(() => {
    const minDelay = new Promise((r) => setTimeout(r, 1800));
    const fetchOffers = async () => {
      try {
        setLoading(true);
        setError("");
        const tenantList = Object.values(DASHBOARD_TENANTS) as DashboardTenantDefinition[];
        const [offersRes, totalsRes, affiliateRes, embeddedSummaries] = await Promise.all([
          fetch(`/api/offers?dateField=timestamp&startDate=${apiDates.startDate}&endDate=${apiDates.endDate}`),
          fetch(`/api/dashboard/summary?scope=all-offers&dateField=timestamp&startDate=${apiDates.startDate}&endDate=${apiDates.endDate}`),
          fetch(`/api/financials/affiliate-payouts?startDate=${apiDates.startDate}&endDate=${apiDates.endDate}`),
          Promise.all(
            tenantList.map(async (dashboard) => {
              const res = await fetch(
                `/api/dashboard/summary?dashboard=${encodeURIComponent(dashboard.slug)}&dateField=timestamp&startDate=${apiDates.startDate}&endDate=${apiDates.endDate}`
              );
              const json = await res.json();
              if (!json.success) {
                return null;
              }

              const metrics = json.data?.metrics || {};
              return {
                _id: dashboard.label,
                totalCalls: metrics.callsDue || 0,
                totalCash: metrics.totalCashCollected || 0,
                totalRevenue: metrics.totalRevenue || 0,
                mrrCash: metrics.mrrCash || 0,
                callsClosed: metrics.callsClosed || 0,
                closeRate: metrics.closeRate || 0,
                showRate: metrics.showRate || 0,
                avgCashPerCall: metrics.avgCashPerCall || 0,
                avgCashPerClose: metrics.avgCashPerClose || 0,
                trend: json.data?.cashTrend || [],
                liveDashboardSlug: dashboard.slug,
              } satisfies OfferData;
            })
          ),
          minDelay,
        ]);
        const offersData = await offersRes.json();
        const totalsData = await totalsRes.json();
        const affiliateData = await affiliateRes.json();

        if (offersData.success && totalsData.success) {
          const embeddedOffers = embeddedSummaries.filter((item): item is OfferData => Boolean(item));
          // An offer is selectable only when it produced cash in the active date range.
          const partnerRevenue = totalsData.data?.partnerRevenue || {};
          const clientOnboardDirectCash = Number(partnerRevenue["Client Onboard"] || 0);
          const agencyOffers = offersData.data.map((item: OfferData) => item._id === "Client Onboard" ? { ...item, totalCash: item.totalCash + clientOnboardDirectCash, totalRevenue: item.totalRevenue + clientOnboardDirectCash } : item);
          const partnerOffers = Object.entries(partnerRevenue)
            .filter(([name]) => name !== "Client Onboard")
            .map(([name, totalCash]) => ({ _id: name, totalCalls: 0, totalCash: Number(totalCash || 0), totalRevenue: Number(totalCash || 0), mrrCash: 0, callsClosed: 0, closeRate: 0, showRate: 0, avgCashPerCall: 0, avgCashPerClose: 0, trend: [] }))
            .filter((item) => item.totalCash > 0);
          const affiliateNet = Number(affiliateData?.data?.net || 0);
          const affiliateOffer = affiliateData?.success && affiliateNet !== 0 ? [{ _id: "Affiliate Payouts", totalCalls: 0, totalCash: affiliateNet, totalRevenue: affiliateNet, mrrCash: 0, callsClosed: 0, closeRate: 0, showRate: 0, avgCashPerCall: 0, avgCashPerClose: 0, trend: [] } satisfies OfferData] : [];
          const combinedOffers = [...agencyOffers.filter((item: OfferData) => !SERVICE_PAYMENT_CATEGORIES.has(item._id) || item._id === "Client Onboard"), ...embeddedOffers, ...partnerOffers, ...affiliateOffer]
            .filter((item) => item.totalCash > 0)
            .sort((a, b) => b.totalCash - a.totalCash);
          setOffers(combinedOffers);

          // Tenants with isExternalOnly=true have data exclusively in their own DB
          // and are NOT already counted in the main infoscalers_eoc total — add their cash separately.
          const baseCash = totalsData.data?.metrics?.totalCashCollected || 0;

          setAllOffersTotals({
            offerCount: combinedOffers.length,
            totalCalls: totalsData.data?.metrics?.callsDue || 0,
            totalCash: baseCash,
            totalRevenue: totalsData.data?.metrics?.totalRevenue || 0,
            mrrCash: totalsData.data?.metrics?.mrrCash || 0,
            callsClosed: totalsData.data?.metrics?.callsClosed || 0,
            closeRate: totalsData.data?.metrics?.closeRate || 0,
            showRate: totalsData.data?.metrics?.showRate || 0,
            avgCashPerCall: totalsData.data?.metrics?.avgCashPerCall || 0,
            avgCashPerClose: totalsData.data?.metrics?.avgCashPerClose || 0,
          });
        } else {
          setError("Failed to load offers");
        }
      } catch {
        setError("Failed to load offers");
      } finally {
        setLoading(false);
      }
    };
    fetchOffers();
  }, [apiDates.endDate, apiDates.startDate]);

  const handleSelectOffer = (offerName: string) => {
    const selectedOffer = offers.find((item) => item._id === offerName);
    if (SERVICE_PAYMENT_CATEGORIES.has(offerName)) {
      setSelectedService(offerName);
      setSelectedServiceTotal(selectedOffer?.totalCash || 0);
      return;
    }
    const embeddedDashboardSlug = selectedOffer?.liveDashboardSlug || getEmbeddedDashboardSlugByOfferName(offerName);
    if (embeddedDashboardSlug) {
      const targetUrl = new URL(
        buildOfferDashboardPath("dashboard", embeddedDashboardSlug),
        window.location.origin
      );
      targetUrl.searchParams.set("startDate", apiDates.startDate);
      targetUrl.searchParams.set("endDate", apiDates.endDate);
      targetUrl.searchParams.set("dateField", "timestamp");
      navigate(`${targetUrl.pathname}${targetUrl.search}`);
      return;
    }

    setOffer(offerName);
    navigate(`/?offers=${encodeURIComponent(offerName)}&startDate=${encodeURIComponent(apiDates.startDate)}&endDate=${encodeURIComponent(apiDates.endDate)}&dateField=timestamp`);
  };

  const handleSelectAll = () => {
    setOffer("all");
    navigate(`/?offers=all&startDate=${encodeURIComponent(apiDates.startDate)}&endDate=${encodeURIComponent(apiDates.endDate)}&dateField=timestamp`);
  };

  const fallbackTotals = useMemo(() => {
    const t = offers.reduce(
      (acc, o) => ({
        totalCalls: acc.totalCalls + o.totalCalls,
        totalCash: acc.totalCash + o.totalCash,
        totalRevenue: acc.totalRevenue + o.totalRevenue,
        mrrCash: acc.mrrCash + (o.mrrCash || 0),
        callsClosed: acc.callsClosed + (o.callsClosed || 0),
      }),
      { totalCalls: 0, totalCash: 0, totalRevenue: 0, mrrCash: 0, callsClosed: 0 }
    );
    return {
      ...t,
      closeRate: t.totalCalls > 0 ? (t.callsClosed / t.totalCalls) * 100 : 0,
      showRate: 0,
      avgCashPerCall: t.totalCalls > 0 ? t.totalCash / t.totalCalls : 0,
      avgCashPerClose: t.callsClosed > 0 ? t.totalCash / t.callsClosed : 0,
    };
  }, [offers]);
  const totals = allOffersTotals ?? fallbackTotals;
  const allOffersCount = allOffersTotals?.offerCount ?? offers.length;
  const dashboardOfferCards = offers.filter((item) => DASHBOARD_OFFERS.has(item._id));
  const otherOfferCards = offers.filter((item) => !DASHBOARD_OFFERS.has(item._id));
  const orderedOfferCards = [...dashboardOfferCards, ...otherOfferCards];

  const chartColors = branding.colors.chart;
  const fallbackChartColor = chartColors[0] ?? branding.colors.primary;
  const brandRgb = hexToRgb(branding.colors.primary);

  return (
    <TooltipProvider>
      <div className="min-h-screen flex flex-col items-center px-6 py-14 relative overflow-hidden">
        {/* Header */}
        <motion.div
          className="text-center mb-12 relative z-10"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <BrandLogo variant="primary" className="h-14 w-auto mx-auto mb-5" />
          <h1 className="text-3xl font-bold text-foreground tracking-tight mb-2">
            Select an Offer
          </h1>
          <p className="text-muted-foreground text-sm">
            Choose which offer to view across all {companyName} dashboards
          </p>
        </motion.div>

        {/* Loading animation */}
        {loading && (
          <motion.div
            className="flex flex-col items-center justify-center py-16"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <MoneyLoader />
            <p className="text-sm text-muted-foreground mt-2 animate-pulse">Loading offers...</p>
          </motion.div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="text-center py-16">
            <p className="text-destructive mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-5 py-2.5 rounded-xl bg-[var(--brand-primary)] text-white text-sm font-medium hover:opacity-90 transition-opacity"
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && (
          <div className="w-full max-w-5xl space-y-10">
            {offers.length > 0 ? (
              <div className="space-y-5">
                <motion.button
                  onClick={handleSelectAll}
                  initial={{ opacity: 0, y: 20, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
                  whileHover={{ y: -3, transition: { duration: 0.2 } }}
                  className={`group relative w-full text-left rounded-2xl overflow-hidden border transition-colors duration-300 cursor-pointer ${
                    offer === "all"
                      ? "border-[var(--brand-primary)]/50"
                      : "border-white/[0.06] hover:border-[var(--brand-primary)]/30"
                  }`}
                  style={{
                    boxShadow: offer === "all"
                      ? `0 0 40px -8px rgba(${brandRgb}, 0.3)`
                      : `0 0 20px -8px rgba(${brandRgb}, 0.1)`,
                  }}
                >
                  <div
                    className="h-1 w-full"
                    style={{ background: `linear-gradient(90deg, ${branding.colors.gradient.start}, ${branding.colors.gradient.end})` }}
                  />

                  <div
                    className="relative z-10 p-6"
                    style={{ background: `linear-gradient(135deg, rgba(${brandRgb}, 0.08), transparent 60%)` }}
                  >
                    <div className="flex items-center gap-3 mb-5">
                      <div
                        className="w-11 h-11 rounded-xl flex items-center justify-center shadow-lg"
                        style={{ background: `linear-gradient(135deg, ${branding.colors.gradient.start}, ${branding.colors.gradient.end})` }}
                      >
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-bold text-foreground">All Offers</h3>
                          {offer === "all" && (
                            <Badge variant="outline" className="border-[var(--brand-primary)]/30 text-[var(--brand-primary)] text-[10px] uppercase tracking-wider">
                              <span className="w-1.5 h-1.5 rounded-full bg-[var(--brand-primary)] animate-pulse mr-1" />
                              Active
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">{allOffersCount} offers combined</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-5">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="cursor-default">
                            <span className="text-[10px] text-muted-foreground/60 uppercase tracking-[0.08em] font-medium block mb-0.5">Cash Collected</span>
                            <span className="text-xl font-bold text-foreground tabular-nums">{formatCurrency(totals.totalCash)}</span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>Total cash across all offers</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="cursor-default">
                            <span className="text-[10px] text-muted-foreground/60 uppercase tracking-[0.08em] font-medium block mb-0.5">Revenue</span>
                            <span className="text-xl font-bold text-foreground tabular-nums">{formatCurrency(totals.totalRevenue)}</span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>Total revenue generated</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="cursor-default">
                            <span className="text-[10px] text-muted-foreground/60 uppercase tracking-[0.08em] font-medium block mb-0.5">MRR</span>
                            <span className="text-xl font-bold text-foreground tabular-nums">{formatCurrency(totals.mrrCash)}</span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>Monthly recurring revenue</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="cursor-default">
                            <span className="text-[10px] text-muted-foreground/60 uppercase tracking-[0.08em] font-medium block mb-0.5">Total Calls</span>
                            <span className="text-xl font-bold text-foreground tabular-nums">{formatNumber(totals.totalCalls)}</span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>Total calls across all offers</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="cursor-default">
                            <span className="text-[10px] text-muted-foreground/60 uppercase tracking-[0.08em] font-medium block mb-0.5">Closed</span>
                            <span className="text-xl font-bold text-foreground tabular-nums">{formatNumber(totals.callsClosed)}</span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>Total calls closed with cash</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="cursor-default">
                            <span className="text-[10px] text-muted-foreground/60 uppercase tracking-[0.08em] font-medium block mb-0.5">Show Rate</span>
                            <span className="text-xl font-bold text-foreground tabular-nums">{formatPercent(totals.showRate)}</span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>Percentage of calls due that were taken</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="cursor-default">
                            <span className="text-[10px] text-muted-foreground/60 uppercase tracking-[0.08em] font-medium block mb-0.5">Close Rate</span>
                            <span className="text-xl font-bold text-foreground tabular-nums">{formatPercent(totals.closeRate)}</span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>Percentage of calls that closed</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="cursor-default">
                            <span className="text-[10px] text-muted-foreground/60 uppercase tracking-[0.08em] font-medium block mb-0.5">Avg $/Close</span>
                            <span className="text-xl font-bold text-foreground tabular-nums">{formatCashPerClose(totals.avgCashPerClose, totals.callsClosed)}</span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>Average cash per closed deal</TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                </motion.button>
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground mb-1">No offers found</p>
                <p className="text-sm text-muted-foreground">
                  Sync data to populate the &ldquo;Cash Source&rdquo; column.
                </p>
              </div>
            )}

            {offers.length > 0 && (
              <div className="space-y-4">
                <div className="mb-4">
                  <h2 className="text-xl font-bold text-foreground">Offer Dashboards</h2>
                  <p className="text-sm text-muted-foreground">Browse all offers and open live client dashboards directly.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {orderedOfferCards.map((o, i) => {
                    const isOtherCard = !DASHBOARD_OFFERS.has(o._id);
                    const isFirstOtherCard = isOtherCard && i === dashboardOfferCards.length;
                    const servicePayments = SERVICE_PAYMENT_DETAILS[o._id] || [];
                    const serviceAov = servicePayments.length > 0 ? o.totalCash / servicePayments.length : 0;
                    const color = chartColors[i % chartColors.length] ?? fallbackChartColor;
                    const rgb = hexToRgb(color);
                    const cashPercent = totals.totalCash > 0 ? (o.totalCash / totals.totalCash) * 100 : 0;
                    const isActive = offer === o._id;

                    return (
                      <Fragment key={o._id}>
                        {isFirstOtherCard && <div className="col-span-full mt-8 border-t border-border/30 pt-8"><h2 className="text-xl font-bold text-foreground">Other</h2><p className="text-sm text-muted-foreground">Service payments and partner payouts — open a card to view each payment.</p></div>}
                      <motion.button
                        onClick={() => handleSelectOffer(o._id)}
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{
                          duration: 0.45,
                          delay: 0.08 + i * 0.05,
                          ease: [0.25, 0.46, 0.45, 0.94],
                        }}
                        whileHover={{ y: -4, transition: { duration: 0.2 } }}
                        className={`group relative text-left rounded-2xl overflow-hidden border transition-colors duration-300 cursor-pointer ${
                          isActive
                            ? "border-[var(--brand-primary)]/50"
                            : "border-white/[0.06] hover:border-white/[0.12]"
                        }`}
                        style={{
                          boxShadow: isActive
                            ? `0 0 40px -8px rgba(${rgb}, 0.4)`
                            : `0 0 20px -8px rgba(${rgb}, 0.12)`,
                        }}
                      >
                        <div
                          className="h-1 w-full"
                          style={{ background: `linear-gradient(90deg, ${color}, ${color}88)` }}
                        />

                        <div
                          className="relative z-10 p-5"
                          style={{ background: `linear-gradient(160deg, rgba(${rgb}, 0.08), rgba(${rgb}, 0.02) 50%, transparent)` }}
                        >
                          <div className="flex items-center gap-3 mb-3">
                            <div
                              className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 font-bold text-sm shadow-sm"
                              style={{ background: `${color}18`, color }}
                            >
                              {o._id.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="text-[15px] font-semibold text-foreground truncate">{OFFER_DISPLAY_NAMES[o._id] || o._id}</h3>
                              <p className="text-[11px] text-muted-foreground">
                                {isOtherCard ? `${formatNumber(servicePayments.length || 1)} payment${servicePayments.length === 1 ? "" : "s"}` : `${formatNumber(o.totalCalls)} calls · ${formatNumber(o.callsClosed || 0)} closed`}
                              </p>
                            </div>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="relative shrink-0 cursor-default">
                                  <ShareRing percent={cashPercent} color={color} />
                                  <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-foreground/60">
                                    {cashPercent.toFixed(0)}%
                                  </span>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>{cashPercent.toFixed(1)}% of total cash</TooltipContent>
                            </Tooltip>
                          </div>

                          <div className="flex items-end justify-between mb-3">
                            <div>
                              <span className="text-2xl font-bold tabular-nums" style={{ color }}>
                                {formatCurrency(o.totalCash)}
                              </span>
                              <span className="text-[11px] text-muted-foreground ml-1.5">cash collected</span>
                            </div>
                            {o.trend && o.trend.length > 1 && (
                              <Sparkline data={o.trend} color={color} />
                            )}
                          </div>

                          <div className="h-px bg-border/20 mb-3" />

                          {isOtherCard ? (
                            <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
                              <div><span className="text-[10px] text-muted-foreground/60 uppercase tracking-[0.06em] block">Revenue</span><span className="text-xs font-semibold text-foreground tabular-nums">{formatCurrency(o.totalRevenue)}</span></div>
                              <div><span className="text-[10px] text-muted-foreground/60 uppercase tracking-[0.06em] block">AOV</span><span className="text-xs font-semibold text-foreground tabular-nums">{serviceAov > 0 ? formatCurrency(serviceAov) : "—"}</span></div>
                            </div>
                          ) : (
                          <div className="grid grid-cols-3 gap-x-4 gap-y-2.5">
                            <div>
                              <span className="text-[10px] text-muted-foreground/60 uppercase tracking-[0.06em] block">Revenue</span>
                              <span className="text-xs font-semibold text-foreground tabular-nums">{formatCurrency(o.totalRevenue)}</span>
                            </div>
                            <div>
                              <span className="text-[10px] text-muted-foreground/60 uppercase tracking-[0.06em] block">Close Rate</span>
                              <span className="text-xs font-semibold text-foreground tabular-nums">{formatPercent(o.closeRate || 0)}</span>
                            </div>
                            <div>
                              <span className="text-[10px] text-muted-foreground/60 uppercase tracking-[0.06em] block">$/Close</span>
                              <span className="text-xs font-semibold text-foreground tabular-nums">
                                {formatCashPerClose(o.avgCashPerClose, o.callsClosed || 0)}
                              </span>
                            </div>
                            <div>
                              <span className="text-[10px] text-muted-foreground/60 uppercase tracking-[0.06em] block">MRR</span>
                              <span className="text-xs font-semibold text-foreground tabular-nums">{formatCurrency(o.mrrCash || 0)}</span>
                            </div>
                            <div>
                              <span className="text-[10px] text-muted-foreground/60 uppercase tracking-[0.06em] block">AOV</span>
                              <span className="text-xs font-semibold text-foreground tabular-nums">
                                {o.callsClosed > 0 ? formatCurrency(o.avgCashPerClose) : "\u2014"}
                              </span>
                            </div>
                            <div>
                              <span className="text-[10px] text-muted-foreground/60 uppercase tracking-[0.06em] block">Show Rate</span>
                              <span className="text-xs font-semibold text-foreground tabular-nums">
                                {formatPercent(o.showRate)}
                              </span>
                            </div>
                          </div>
                          )}

                          <div className="mt-4 h-1 rounded-full bg-white/[0.04] overflow-hidden">
                            <motion.div
                              className="h-full rounded-full"
                              style={{ background: color }}
                              initial={{ width: 0 }}
                              animate={{ width: `${Math.max(cashPercent, 1)}%` }}
                              transition={{ duration: 0.8, delay: 0.3 + i * 0.05, ease: "easeOut" }}
                            />
                          </div>
                        </div>

                        {isActive && (
                          <div className="absolute top-4 right-3 z-20">
                            <Badge variant="outline" className="border-[var(--brand-primary)]/30 text-[var(--brand-primary)] text-[9px] uppercase tracking-wider px-1.5 py-0">
                              <motion.span
                                className="w-1.5 h-1.5 rounded-full bg-[var(--brand-primary)] mr-1"
                                animate={{ opacity: [1, 0.3, 1] }}
                                transition={{ duration: 2, repeat: Infinity }}
                              />
                              Active
                            </Badge>
                          </div>
                        )}
                      </motion.button>
                      </Fragment>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {selectedService && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setSelectedService(null)}>
          <div className="w-full max-w-lg rounded-2xl border border-white/15 bg-background p-6 shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="mb-5 flex items-start justify-between"><div><h2 className="text-xl font-bold">{selectedService}</h2><p className="text-sm text-muted-foreground">Payment distribution for the selected period</p></div><button className="text-muted-foreground hover:text-foreground" onClick={() => setSelectedService(null)}>✕</button></div>
            <div className="space-y-2">{(SERVICE_PAYMENT_DETAILS[selectedService] || []).map((payment, index) => <div key={`${payment.name}-${payment.amount}-${index}`} className="flex items-center justify-between rounded-lg border border-border/50 px-4 py-3"><span className="font-medium">{payment.name}{payment.detail ? <span className="ml-2 text-xs font-normal text-muted-foreground">{payment.detail}</span> : null}</span><span className="font-semibold">{formatCurrency(payment.amount)}</span></div>)}</div>
            <div className="mt-5 flex justify-between border-t border-border/50 pt-4 font-semibold"><span>Total collected</span><span>{formatCurrency(selectedServiceTotal || (SERVICE_PAYMENT_DETAILS[selectedService] || []).reduce((sum, payment) => sum + payment.amount, 0))}</span></div>
          </div>
        </div>
      )}
    </TooltipProvider>
  );
}
